import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { AuthImage } from '../wom/AuthImage';
import {
  X, Pencil, Save, RotateCcw, Upload, Camera, Trash2, User, History,
  ShieldCheck, Wrench, ClipboardList, Image as ImageIcon, AlertTriangle, Archive,
} from 'lucide-react';
import { toast } from 'sonner';

const NAVY = '#0E2A47';
const GREEN = '#8FC93A';

const CATEGORY_LABELS = {
  front: 'Front', rear: 'Rear', driver_side: 'Driver Side', passenger_side: 'Passenger Side',
  interior: 'Interior', dashboard_odometer: 'Dashboard / Odometer', engine: 'Engine Compartment',
  tires_wheels: 'Tires & Wheels', doors_cargo: 'Doors & Cargo', signage_decals: 'Signage / Decals', other: 'Other / Damage',
};

const inputCls = 'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-[#0E2A47] focus:outline-none disabled:bg-slate-50 disabled:text-slate-500';
const lbl = 'mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500';

const fmtDate = (d) => {
  if (!d) return '—';
  const s = String(d);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[2]}/${m[3]}/${m[1].slice(-2)}`;
  const dt = new Date(s);
  if (isNaN(dt.getTime())) return '—';
  return `${String(dt.getMonth() + 1).padStart(2, '0')}/${String(dt.getDate()).padStart(2, '0')}/${String(dt.getFullYear()).slice(-2)}`;
};

// Master-record field definitions grouped by section. type: text|date|number|select|textarea
const MASTER_FIELDS = {
  identity: [
    ['veh_number', 'Vehicle #', 'text'], ['make', 'Make', 'text'], ['model', 'Model', 'text'],
    ['trim', 'Trim / Series', 'text'], ['year', 'Model Year', 'number'],
    ['vin', 'VIN', 'text'], ['tag', 'License Plate / Tag', 'text'], ['tag_state', 'Tag State / Jurisdiction', 'text'],
    ['registration_expiration', 'Registration Expiration', 'date'],
  ],
  acquisition: [
    ['acquisition_date', 'Acquisition Date', 'date'], ['in_service_date', 'Placed into TFS Service', 'date'],
    ['acquisition_type', 'Acquisition Type', 'select', ['Purchased', 'Leased', 'Other']],
    ['starting_odometer', 'Starting Odometer', 'number'], ['starting_odometer_date', 'Starting Odometer Date', 'date'],
    ['odometer_unit', 'Odometer Unit', 'select', ['miles', 'kilometers']],
    ['purchase_price', 'Purchase Price', 'number'], ['warranty_expiration', 'Warranty Expiration', 'date'],
    ['acquisition_notes', 'Notes', 'textarea'],
  ],
  specs: [
    ['body_style', 'Body Style', 'text'], ['fuel_type', 'Engine / Fuel Type', 'text'],
    ['engine_displacement', 'Engine Displacement / Code', 'text'], ['transmission', 'Transmission', 'text'],
    ['drivetrain', 'Drivetrain', 'text'], ['exterior_color', 'Exterior Color', 'text'],
    ['tire_size', 'Tire Size', 'text'], ['oil_spec', 'Recommended Oil Spec', 'text'],
    ['maintenance_ref', 'Maintenance Manual / Schedule Ref', 'text'],
  ],
  baseline: [
    ['baseline_condition_date', 'Baseline Condition Date', 'date'],
    ['baseline_odometer', 'Baseline Odometer', 'number'], ['baseline_odometer_date', 'Baseline Odometer Date', 'date'],
    ['baseline_condition_summary', 'Baseline Condition Summary', 'textarea'],
    ['baseline_inspection_by', 'Baseline Inspection Completed By', 'text'],
    ['mechanic_confirmation', 'Mechanic Working-Order Confirmation', 'select', ['Confirmed', 'Issues found', 'Pending']],
    ['mechanic_confirmation_date', 'Confirmation Date', 'date'], ['mechanic_name', 'Mechanic', 'text'],
    ['mechanic_notes', 'Mechanic Notes', 'textarea'],
  ],
};

const Section = ({ icon: Icon, title, subtitle, children, testId }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid={testId}>
    <div className="mb-4 flex items-center gap-2">
      <Icon size={18} style={{ color: GREEN }} />
      <h3 className="text-base font-bold" style={{ color: NAVY }}>{title}</h3>
    </div>
    {subtitle && <p className="-mt-3 mb-4 text-xs text-slate-400">{subtitle}</p>}
    {children}
  </div>
);

function FieldGrid({ fields, draft, setField, editing }) {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {fields.map(([key, label, type, opts]) => {
        const val = draft[key];
        const isWide = type === 'textarea';
        return (
          <div key={key} className={isWide ? 'sm:col-span-2 lg:col-span-3' : ''}>
            <label className={lbl}>{label}</label>
            {!editing ? (
              <div className="min-h-[36px] rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-700" data-testid={`vp-view-${key}`}>
                {type === 'date' ? fmtDate(val) : (val === 0 || val ? String(val) : <span className="text-slate-400">— not verified —</span>)}
              </div>
            ) : type === 'select' ? (
              <select className={inputCls} value={val ?? ''} onChange={(e) => setField(key, e.target.value || null)} data-testid={`vp-edit-${key}`}>
                <option value="">—</option>
                {opts.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            ) : type === 'textarea' ? (
              <textarea rows={2} className={inputCls} value={val ?? ''} onChange={(e) => setField(key, e.target.value)} data-testid={`vp-edit-${key}`} />
            ) : (
              <input type={type === 'number' ? 'number' : (type === 'date' ? 'date' : 'text')}
                className={inputCls} value={val ?? ''} maxLength={key === 'vin' ? 17 : undefined}
                onChange={(e) => setField(key, type === 'number' ? (e.target.value === '' ? null : Number(e.target.value)) : e.target.value)}
                data-testid={`vp-edit-${key}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

const VehicleProfile = ({ vehicleId, canManage, drivers = [], meta, refreshKey = 0, onRetire, onClose, onSaved }) => {
  const { token } = useContext(AuthContext);
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [vehicle, setVehicle] = useState(null);
  const [draft, setDraft] = useState({});
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [photoCat, setPhotoCat] = useState('front');
  const [photoCaption, setPhotoCaption] = useState('');
  const [photoOdo, setPhotoOdo] = useState('');
  const [uploading, setUploading] = useState(false);
  const [asg, setAsg] = useState({ assigned_driver_id: '', assignment_start_date: '', assignment_end_date: '', assigned_location: '', assignment_notes: '' });
  const [savingAsg, setSavingAsg] = useState(false);
  const chooseRef = useRef(null);
  const cameraRef = useRef(null);

  const loadVehicle = useCallback(() => {
    axios.get(`${API}/vehicles/${vehicleId}`, auth).then((r) => {
      setVehicle(r.data); setDraft(r.data);
      setAsg({
        assigned_driver_id: r.data.assigned_driver_id || '',
        assignment_start_date: r.data.assignment_start_date || '',
        assignment_end_date: r.data.assignment_end_date || '',
        assigned_location: r.data.assigned_location || '',
        assignment_notes: r.data.assignment_notes || '',
      });
    }).catch(() => toast.error('Failed to load vehicle'));
  }, [vehicleId, token]);

  const loadPhotos = useCallback(() => {
    axios.get(`${API}/vehicles/${vehicleId}/photos`, auth).then((r) => setPhotos(r.data || [])).catch(() => {});
  }, [vehicleId, token]);

  useEffect(() => { loadVehicle(); loadPhotos(); }, [loadVehicle, loadPhotos, refreshKey]);

  const setField = (k, v) => setDraft((p) => ({ ...p, [k]: v }));

  const cancelEdit = () => { setDraft(vehicle); setEditing(false); };

  const saveMaster = async () => {
    setSaving(true);
    try {
      const keys = [].concat(...Object.values(MASTER_FIELDS)).map((f) => f[0]);
      const body = {};
      keys.forEach((k) => { if (draft[k] !== vehicle[k]) body[k] = draft[k] === '' ? null : draft[k]; });
      if (Object.keys(body).length === 0) { setEditing(false); setSaving(false); return; }
      const r = await axios.put(`${API}/vehicles/${vehicleId}`, body, auth);
      setVehicle(r.data); setDraft(r.data); setEditing(false);
      toast.success('Vehicle master record saved');
      onSaved && onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const saveAssignment = async () => {
    setSavingAsg(true);
    try {
      const body = {
        assigned_driver_id: asg.assigned_driver_id || null,
        assignment_start_date: asg.assignment_start_date || null,
        assignment_end_date: asg.assignment_end_date || null,
        assigned_location: asg.assigned_location || null,
        assignment_notes: asg.assignment_notes || null,
      };
      const r = await axios.put(`${API}/vehicles/${vehicleId}/assignment`, body, auth);
      setVehicle(r.data); setDraft(r.data);
      toast.success(body.assigned_driver_id ? 'Driver assignment saved' : 'Set to shared/pool (no driver)');
      onSaved && onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Assignment save failed');
    } finally { setSavingAsg(false); }
  };

  const doUpload = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('category', photoCat);
        fd.append('caption', photoCaption);
        if (photoOdo) fd.append('odometer', photoOdo);
        await axios.post(`${API}/vehicles/${vehicleId}/photos`, fd, { headers: { ...auth.headers, 'Content-Type': 'multipart/form-data' } });
      }
      toast.success('Photo(s) uploaded');
      setPhotoCaption(''); setPhotoOdo('');
      loadPhotos();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };

  const deletePhoto = async (photoId) => {
    try {
      await axios.delete(`${API}/vehicles/${vehicleId}/photos/${photoId}`, auth);
      toast.success('Photo removed'); loadPhotos();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  if (!vehicle) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
        <div className="rounded-xl bg-white px-6 py-4 text-slate-500">Loading vehicle…</div>
      </div>
    );
  }

  const active = (vehicle.status || 'active') === 'active';

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/40 p-0 sm:p-4" data-testid="vehicle-profile">
      <div className="mx-auto min-h-full w-full max-w-5xl bg-slate-50 shadow-2xl sm:min-h-0 sm:rounded-2xl">
        {/* Sticky header */}
        <div className="sticky top-0 z-10 flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 bg-white px-5 py-4 sm:rounded-t-2xl">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="rounded-md px-2 py-0.5 text-sm font-extrabold text-white" style={{ background: NAVY }} data-testid="vp-veh-number">#{vehicle.veh_number}</span>
              <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${active ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-500'}`}>{active ? 'Active' : 'Retired'}</span>
            </div>
            <h2 className="mt-1 truncate text-lg font-bold" style={{ color: NAVY }}>{vehicle.year} {vehicle.make} {vehicle.model}{vehicle.trim ? ` ${vehicle.trim}` : ''}</h2>
          </div>
          <div className="flex items-center gap-2">
            {canManage && !editing && (
              <button onClick={() => setEditing(true)} data-testid="vp-edit-btn" className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold text-white" style={{ background: NAVY }}><Pencil size={15} /> Edit</button>
            )}
            {canManage && !editing && onRetire && (
              <button onClick={() => onRetire(vehicle)} data-testid="vp-retire-btn" className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-semibold ${active ? 'border-amber-300 text-amber-700 hover:bg-amber-50' : 'border-green-300 text-green-700 hover:bg-green-50'}`}>
                {active ? <><Archive size={15} /> Retire</> : <><RotateCcw size={15} /> Reactivate</>}
              </button>
            )}
            {canManage && editing && (
              <>
                <button onClick={saveMaster} disabled={saving} data-testid="vp-save-btn" className="inline-flex items-center gap-1.5 rounded-lg bg-[#8FC93A] px-3 py-2 text-sm font-semibold text-[#0E2A47] disabled:opacity-50"><Save size={15} /> {saving ? 'Saving…' : 'Save'}</button>
                <button onClick={cancelEdit} data-testid="vp-cancel-btn" className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-600"><RotateCcw size={15} /> Cancel</button>
              </>
            )}
            <button onClick={onClose} data-testid="vp-close-btn" className="rounded-md p-2 text-slate-500 hover:bg-slate-100"><X size={20} /></button>
          </div>
        </div>

        <div className="space-y-5 p-5">
          {/* Operations Hub read-only panel */}
          <div className="rounded-xl border border-indigo-200 bg-indigo-50/60 p-4" data-testid="vp-ops-panel">
            <div className="mb-2 flex items-center gap-2 text-sm font-bold text-indigo-900"><ShieldCheck size={16} /> Operations Hub — Live Operating Status (read-only)</div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <div><div className={lbl}>Latest Odometer</div><div className="text-sm font-semibold text-slate-700" data-testid="vp-ops-odometer">{vehicle.ops_latest_odometer != null ? `${vehicle.ops_latest_odometer} ${vehicle.odometer_unit || 'miles'}` : <span className="text-slate-400">No Operations data yet</span>}</div></div>
              <div><div className={lbl}>Latest Inspection</div><div className="text-sm font-semibold text-slate-700" data-testid="vp-ops-inspection-date">{vehicle.ops_latest_inspection_date ? fmtDate(vehicle.ops_latest_inspection_date) : <span className="text-slate-400">No Operations data yet</span>}</div></div>
              <div><div className={lbl}>Inspection Status</div><div className="text-sm font-semibold text-slate-700" data-testid="vp-ops-inspection-status">{vehicle.ops_latest_inspection_status || <span className="text-slate-400">—</span>}</div></div>
              <div><div className={lbl}>Mechanic Sign-off (Baseline)</div><div className="text-sm font-semibold text-slate-700">{vehicle.mechanic_confirmation || <span className="text-slate-400">—</span>}</div></div>
            </div>
            <p className="mt-2 text-[11px] text-indigo-700/80">Sourced live from Operations Hub inspections/maintenance. The <b>baseline</b> odometer &amp; condition below are a dated snapshot at intake — not the vehicle's current mileage or condition.</p>
          </div>

          <Section icon={ClipboardList} title="1 · Vehicle Identity" testId="vp-section-identity">
            <FieldGrid fields={MASTER_FIELDS.identity} draft={draft} setField={setField} editing={editing} />
            {editing && <p className="mt-2 text-[11px] text-slate-400">VIN is validated to 17 characters (uppercase; excludes I, O, Q) and must be unique. Vehicle # is text so leading zeros are preserved.</p>}
          </Section>

          <Section icon={Wrench} title="2 · Acquisition & Service Life" testId="vp-section-acquisition">
            <FieldGrid fields={MASTER_FIELDS.acquisition} draft={draft} setField={setField} editing={editing} />
          </Section>

          {/* Assignment */}
          <Section icon={User} title="3 · Assignment" subtitle="Leave the driver blank for a shared/pool vehicle. Changing the driver retains the prior assignment as history." testId="vp-section-assignment">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <div>
                <label className={lbl}>Assigned Driver</label>
                <select className={inputCls} value={asg.assigned_driver_id} disabled={!canManage} onChange={(e) => setAsg((p) => ({ ...p, assigned_driver_id: e.target.value }))} data-testid="vp-assign-driver">
                  <option value="">Unassigned (shared / pool)</option>
                  {drivers.map((d) => <option key={d.id} value={d.id}>{d.name}{d.personnel_number ? ` (#${d.personnel_number})` : ''}</option>)}
                </select>
              </div>
              <div><label className={lbl}>Assignment Start</label><input type="date" className={inputCls} value={asg.assignment_start_date ? String(asg.assignment_start_date).slice(0, 10) : ''} disabled={!canManage} onChange={(e) => setAsg((p) => ({ ...p, assignment_start_date: e.target.value }))} data-testid="vp-assign-start" /></div>
              <div><label className={lbl}>Assignment End / Return</label><input type="date" className={inputCls} value={asg.assignment_end_date ? String(asg.assignment_end_date).slice(0, 10) : ''} disabled={!canManage} onChange={(e) => setAsg((p) => ({ ...p, assignment_end_date: e.target.value }))} data-testid="vp-assign-end" /></div>
              <div><label className={lbl}>Location / Normal Parking</label><input className={inputCls} value={asg.assigned_location} disabled={!canManage} onChange={(e) => setAsg((p) => ({ ...p, assigned_location: e.target.value }))} data-testid="vp-assign-location" /></div>
              <div className="sm:col-span-2"><label className={lbl}>Assignment Notes</label><input className={inputCls} value={asg.assignment_notes} disabled={!canManage} onChange={(e) => setAsg((p) => ({ ...p, assignment_notes: e.target.value }))} data-testid="vp-assign-notes" /></div>
            </div>
            {canManage && (
              <button onClick={saveAssignment} disabled={savingAsg} data-testid="vp-assign-save" className="mt-3 inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold text-white disabled:opacity-50" style={{ background: NAVY }}><Save size={14} /> {savingAsg ? 'Saving…' : 'Save Assignment'}</button>
            )}
            {(vehicle.assignment_history || []).length > 0 && (
              <div className="mt-4 border-t border-slate-100 pt-3">
                <div className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-500"><History size={13} /> Prior Assignments</div>
                <div className="space-y-1.5" data-testid="vp-assign-history">
                  {(vehicle.assignment_history || []).slice().reverse().map((h, i) => (
                    <div key={i} className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
                      <b>{h.assigned_driver_name || 'Unassigned'}</b> · {fmtDate(h.assignment_start_date)} → {fmtDate(h.assignment_end_date)}{h.assigned_location ? ` · ${h.assigned_location}` : ''}{h.ended_by ? ` · ended by ${h.ended_by}` : ''}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Section>

          <Section icon={Wrench} title="4 · Vehicle Specifications" subtitle="Leave blank until verified against the VIN, vehicle label, or manufacturer information — values are never inferred." testId="vp-section-specs">
            <FieldGrid fields={MASTER_FIELDS.specs} draft={draft} setField={setField} editing={editing} />
          </Section>

          <Section icon={ShieldCheck} title="5 · Baseline Condition" testId="vp-section-baseline">
            <FieldGrid fields={MASTER_FIELDS.baseline} draft={draft} setField={setField} editing={editing} />
          </Section>

          {/* Photo library */}
          <Section icon={ImageIcon} title="Baseline Photo Library & History" subtitle="Chronological. Admin baseline photos plus any Operations Hub inspection photos linked to this vehicle. Each photo keeps its date, uploader, category, odometer and caption." testId="vp-section-photos">
            {canManage && (
              <div className="mb-4 rounded-xl border border-slate-200 bg-slate-50 p-3">
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-4">
                  <div><label className={lbl}>Category</label>
                    <select className={inputCls} value={photoCat} onChange={(e) => setPhotoCat(e.target.value)} data-testid="vp-photo-category">
                      {(meta?.photo_categories || Object.keys(CATEGORY_LABELS)).map((c) => <option key={c} value={c}>{CATEGORY_LABELS[c] || c}</option>)}
                    </select>
                  </div>
                  <div><label className={lbl}>Caption</label><input className={inputCls} value={photoCaption} onChange={(e) => setPhotoCaption(e.target.value)} data-testid="vp-photo-caption" placeholder="Optional" /></div>
                  <div><label className={lbl}>Odometer (optional)</label><input type="number" className={inputCls} value={photoOdo} onChange={(e) => setPhotoOdo(e.target.value)} data-testid="vp-photo-odometer" /></div>
                  <div className="flex items-end gap-2">
                    <button onClick={() => chooseRef.current?.click()} disabled={uploading} data-testid="vp-choose-photos" className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-[#0E2A47] px-3 py-2 text-sm font-semibold text-[#0E2A47] disabled:opacity-50"><Upload size={14} /> {uploading ? 'Uploading…' : 'Choose Photos'}</button>
                    <button onClick={() => cameraRef.current?.click()} disabled={uploading} data-testid="vp-take-photo" className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold text-white disabled:opacity-50" style={{ background: NAVY }}><Camera size={14} /> Take Photo</button>
                  </div>
                </div>
                <input ref={chooseRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => { doUpload(e.target.files); e.target.value = ''; }} />
                <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { doUpload(e.target.files); e.target.value = ''; }} />
              </div>
            )}
            {photos.length === 0 ? (
              <div className="rounded-lg bg-slate-50 py-10 text-center text-sm text-slate-400" data-testid="vp-photos-empty">No photos yet.</div>
            ) : (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4" data-testid="vp-photo-grid">
                {photos.map((p) => (
                  <div key={p.id} className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                    <div className="relative">
                      <AuthImage src={`${API}/vehicles/${vehicleId}/photos/${p.id}/image`} token={token} alt={p.caption} caption={p.caption} className="h-32 w-full" testId={`vp-photo-${p.id}`} />
                      {p.source === 'operations_inspection' && <span className="absolute left-1 top-1 rounded bg-indigo-600/90 px-1.5 py-0.5 text-[9px] font-bold text-white">OPS</span>}
                      {canManage && p.source !== 'operations_inspection' && (
                        <button onClick={() => deletePhoto(p.id)} data-testid={`vp-photo-delete-${p.id}`} className="absolute right-1 top-1 rounded bg-white/90 p-1 text-red-600 hover:bg-white"><Trash2 size={13} /></button>
                      )}
                    </div>
                    <div className="p-2 text-[11px] text-slate-500">
                      <div className="font-semibold text-slate-700">{CATEGORY_LABELS[p.category] || p.category}</div>
                      {p.caption && <div className="truncate">{p.caption}</div>}
                      <div>{fmtDate(p.photo_date || p.uploaded_at)}{p.odometer != null ? ` · ${p.odometer} mi` : ''}</div>
                      <div className="truncate text-slate-400">{p.uploaded_by_name || ''}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Section>
        </div>
      </div>
    </div>
  );
};

export default VehicleProfile;
